const PHONE_PATTERN = /^[+\d][\d\s()\-]{6,}$/;
const FIELD_LABELS = {
  name: 'Имя',
  phone: 'Телефон',
  consent: 'Согласие на обработку данных',
};

const formatPhone = (value) => {
  let digits = (value || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('8')) digits = `7${digits.slice(1)}`;
  if (!digits.startsWith('7')) digits = `7${digits}`;
  digits = digits.slice(0, 11);

  let formatted = '+7';
  if (digits.length > 1) formatted += ` (${digits.slice(1, 4)}`;
  if (digits.length >= 4) formatted += ')';
  if (digits.length > 4) formatted += ` ${digits.slice(4, 7)}`;
  if (digits.length >= 7) formatted += `-${digits.slice(7, 9)}`;
  if (digits.length >= 9) formatted += `-${digits.slice(9, 11)}`;
  return formatted;
};

const initPhoneMask = (input) => {
  input.value = formatPhone(input.value);
  input.addEventListener('input', () => {
    const cursorAtEnd = input.selectionStart === input.value.length;
    input.value = formatPhone(input.value);
    if (cursorAtEnd) input.setSelectionRange(input.value.length, input.value.length);
  });
};

const setFieldError = (field, message) => {
  if (!field) return;
  const input = field.querySelector('input');
  if (message) {
    field.setAttribute('data-error', '');
    if (input) input.setAttribute('aria-invalid', 'true');
  } else {
    field.removeAttribute('data-error');
    if (input) input.removeAttribute('aria-invalid');
  }
};

const countDigits = (value) => (value || '').replace(/\D/g, '').length;

const updateSubmitState = (form) => {
  const submit = form.querySelector('.callback-form__submit');
  if (!submit) return;
  const loading = form.dataset.state === 'loading';
  const success = form.dataset.state === 'success';
  submit.disabled = loading;
  submit.hidden = success;
};

const validateForm = (form) => {
  const nameField = form.querySelector('[data-field="name"]');
  const phoneField = form.querySelector('[data-field="phone"]');
  const consentField = form.querySelector('[data-field="consent"]');
  const nameInput = nameField?.querySelector('input');
  const phoneInput = phoneField?.querySelector('input');
  const consentInput = consentField?.querySelector('input');

  const messages = [];
  let firstError = null;
  setFieldError(nameField, null);
  setFieldError(phoneField, null);
  setFieldError(consentField, null);

  const name = (nameInput?.value || '').trim();
  if (name.length < 2) {
    messages.push(name ? 'Имя должно содержать минимум 2 буквы.' : 'Введите имя.');
    setFieldError(nameField, true);
    firstError = firstError || nameInput;
  }

  const phone = (phoneInput?.value || '').replace(/\s/g, '');
  if (!PHONE_PATTERN.test(phone) || countDigits(phone) < 10) {
    messages.push(
      phone
        ? 'Проверьте телефон: введите номер в формате +7 (___) ___-__-__.'
        : 'Введите телефон.',
    );
    setFieldError(phoneField, true);
    firstError = firstError || phoneInput;
  }

  if (!consentInput?.checked) {
    messages.push('Подтвердите согласие на обработку персональных данных.');
    setFieldError(consentField, true);
    firstError = firstError || consentInput;
  }

  return { firstError, messages };
};

const getErrorMessage = (value) => {
  const error = Array.isArray(value) ? value[0] : value;
  if (error && typeof error === 'object') {
    return error.message || 'Проверьте значение.';
  }
  return String(error || 'Проверьте значение.');
};

const formatServerErrors = (errors) => {
  if (!errors || typeof errors !== 'object') return [];
  return Object.entries(errors).map(([key, value]) => {
    const message = getErrorMessage(value);
    const label = FIELD_LABELS[key] || 'Форма';
    return `${label}: ${message}`;
  });
};

const formatErrorStatus = (messages) => messages.join(' ');

const setStatus = (form, message, tone) => {
  const status = form.querySelector('[data-form-status]');
  if (!status) return;
  if (form.dataset.state === 'success') return;
  status.textContent = message || '';
  if (tone) status.setAttribute('data-tone', tone);
  else status.removeAttribute('data-tone');
};

const getCsrfToken = (form) => {
  const match = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  if (match) return decodeURIComponent(match[1]);
  const input = form.querySelector('input[name="csrfmiddlewaretoken"]');
  return input?.value || '';
};

export const synchronizeCsrfToken = (data, csrf) => {
  if (csrf) data.set('csrfmiddlewaretoken', csrf);
};

const init = () => {
  document.querySelectorAll('[data-callback-form]').forEach((form) => {
    if (form.dataset.callbackInit === '1') return;
    form.dataset.callbackInit = '1';

    updateSubmitState(form);

    const phoneInput = form.querySelector('[data-field="phone"] input');
    if (phoneInput) initPhoneMask(phoneInput);

    const fields = form.querySelectorAll('[data-field]');
    fields.forEach((f) => {
      const input = f.querySelector('input');
      if (!input) return;
      const handler = () => {
        setFieldError(f, null);
        updateSubmitState(form);
      };
      input.addEventListener('input', handler);
      input.addEventListener('change', handler);
    });

    form.addEventListener('reset', () => {
      window.requestAnimationFrame(() => updateSubmitState(form));
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (form.dataset.state === 'loading' || form.dataset.state === 'success') return;

      const { firstError, messages } = validateForm(form);
      if (firstError) {
        setStatus(form, formatErrorStatus(messages), 'error');
        firstError.focus();
        updateSubmitState(form);
        return;
      }

      const data = new FormData(form);
      const csrf = getCsrfToken(form);
      // The form lives inside a shared page fragment cache, so its hidden
      // token may belong to the request that populated the cache. Django
      // checks the POST field before X-CSRFToken; keep both values current.
      synchronizeCsrfToken(data, csrf);

      form.dataset.state = 'loading';
      updateSubmitState(form);
      setStatus(form, 'Отправляем…');

      try {
        const response = await fetch(form.action, {
          method: 'POST',
          body: data,
          credentials: 'same-origin',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRFToken': csrf,
          },
        });

        if (response.ok) {
          if (typeof window.__triviaMetrikaSend === 'function') {
            window.__triviaMetrikaSend('callback_submit', { section: 'contacts' });
          }
          const thanksUrl = new URL('/thanks/', window.location.origin);
          thanksUrl.searchParams.set(
            'return_to',
            `${window.location.pathname}${window.location.search}`,
          );
          window.location.assign(thanksUrl.toString());
          return;
        }

        let payload = null;
        try {
          payload = await response.json();
        } catch (err) {
          payload = null;
        }

        const serverErrors = formatServerErrors(payload?.errors);
        if (serverErrors.length) {
          Object.entries(payload.errors).forEach(([key, message]) => {
            const field = form.querySelector(`[data-field="${key}"]`);
            if (field) setFieldError(field, getErrorMessage(message));
          });
        }

        form.dataset.state = 'error';
        setStatus(
          form,
          serverErrors.length
            ? formatErrorStatus(serverErrors)
            : payload?.detail || 'Не удалось отправить заявку. Попробуйте ещё раз или позвоните.',
          'error',
        );
        if (typeof window.__triviaMetrikaSend === 'function') {
          window.__triviaMetrikaSend('callback_submit_error', {
            section: 'contacts',
            status: response.status,
          });
        }
        const firstError = form.querySelector('[data-field][data-error] input');
        if (firstError) firstError.focus();
      } catch (err) {
        form.dataset.state = 'error';
        setStatus(
          form,
          'Нет связи с сервером. Попробуйте ещё раз или позвоните в отдел продаж.',
          'error',
        );
        if (typeof window.__triviaMetrikaSend === 'function') {
          window.__triviaMetrikaSend('callback_submit_error', {
            section: 'contacts',
            status: 'network',
          });
        }
      } finally {
        if (form.dataset.state !== 'success') {
          form.dataset.state = 'idle';
          updateSubmitState(form);
        }
      }
    });
  });
};

export default init;
